import { useTranslation } from 'react-i18next';

import {
  Box,
  Paper,
  Table,
  Stack,
  SvgIcon,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
} from '@mui/material';

import { Loading } from '@/components';
import { CustomerHolidaysHoursDuringHolidays } from '@/features/customerHolidaysHoursDuringHolidays';
import { FolderLine } from '@/features/folders/FolderLine';
import {
  stylesFolders,
  stylesFoldersPaper,
  stylesFoldersTableHeader,
} from '@/features/folders/FoldersTable/FoldersTable.styles';
import { TablePagination } from '@/features/folders/tablePagination';
import { ORDER_TYPE } from '@/features/folders/types';
import { isBorderCrossingRoRo, isBorderCrossingRoRoEmptyV2 } from '@/features/shared/helpers/typologies';
import { getFolderStatusLevelByKey } from '@/utils/folderStatusUtils';
import { OrderByAscIcon, OrderByDescIcon } from '@/utils/iconSVG';

import { useFoldersTable } from './hooks';

export function FoldersTable() {
  const { t } = useTranslation('folders');

  const {
    isGetCarrierBorderCrossingServiceAssociationReferentialLoading,
    isGetFoldersStatusReferentialLoading,
    isGetFoldersLoading,
    isBpeEnabled,
    folders,
    order,
    handleChangePage,
    getFolderStatusById,
    handleChangePageSize,
    handleSortByCrossingDate,
  } = useFoldersTable();

  const folderStatusSuffix = (folderTypologyId: string) => {
    if (isBpeEnabled) {
      return '';
    }
    if (isBorderCrossingRoRo({ folderTypologyId })) {
      return 'BORDER_CROSSING_RORO.';
    }
    if (isBorderCrossingRoRoEmptyV2({ folderTypologyId })) {
      return 'BORDER_CROSSING_RORO_EMPTY_V2.';
    }
    return '';
  };

  return (
    <Stack direction='column' gap={3}>
      <CustomerHolidaysHoursDuringHolidays />
      <Box sx={stylesFolders}>
        <Typography variant='h1' mb={3}>
          {t('folders.foldersListTitle')}&nbsp;-&nbsp;{t('folders.foldersListSubTitle')}
        </Typography>
        <TableContainer component={Paper} elevation={3} sx={stylesFoldersPaper}>
          {isGetFoldersLoading ||
          isGetFoldersStatusReferentialLoading ||
          isGetCarrierBorderCrossingServiceAssociationReferentialLoading ? (
            <Loading message={t('folders.foldersListLoadingMessage')} />
          ) : (
            <Table sx={{ minWidth: 650 }} size='small' stickyHeader>
              <TableHead sx={stylesFoldersTableHeader}>
                <TableRow>
                  <TableCell sx={{ width: '5%' }}>{t('folders.directionCrossing')}</TableCell>

                  <TableCell sx={{ width: '15%' }}>{t('folders.selectedService')}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '20%',
                      cursor: 'pointer',
                    }}
                    onClick={handleSortByCrossingDate}
                  >
                    {t('folders.crossingRequestedDate')}
                    <SvgIcon
                      sx={{
                        position: 'relative',
                        left: '1rem',
                        top: '0.5rem',
                      }}
                      component={order.type === ORDER_TYPE.DESC ? OrderByDescIcon : OrderByAscIcon}
                    />
                  </TableCell>
                  <TableCell>
                    {t('folders.internalReference')}
                    <br />
                    {t('folders.folderReference')}
                  </TableCell>
                  <TableCell>{t('folders.registrations')}</TableCell>
                  {isBpeEnabled && <TableCell>{t('folders.bookingTransportReference')}</TableCell>}
                  <TableCell>{t('folders.status')}</TableCell>
                  <TableCell>{t('folders.tag')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {folders.map((folder) => (
                  <FolderLine
                    key={folder.folderId}
                    folder={folder}
                    folderStatusLabel={t(
                      `folders.status.${folderStatusSuffix(folder.folderTypologyId)}${
                        getFolderStatusById(folder.folderStatusId)?.translationKey
                      }`,
                    )}
                    folderStatusColor={getFolderStatusLevelByKey(
                      `${folderStatusSuffix(folder.folderTypologyId)}${
                        getFolderStatusById(folder.folderStatusId)?.translationKey
                      }`,
                    )}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination handleChangePageSize={handleChangePageSize} handleChangePage={handleChangePage} />
      </Box>
    </Stack>
  );
}
